import { useAuthStore } from "@/state/pinia";

export default [
  // ===== Dashboard =====
  {
    path: "/",
    name: "default",
    meta: { title: "Dashboard", authRequired: true },
    component: () => import("../views/dashboard/Dashboard.vue")
  },

  // ===== Auth =====
  {
    path: "/login",
    name: "login",
    component: () => import("../views/account/login"),
    meta: {
      title: "Login",
      beforeResolve(routeTo, routeFrom, next) {
        const auth = useAuthStore();
        if (auth.getToken()) {
          next({ name: "default" });
        } else {
          next();
        }
      }
    }
  },
  {
    path: "/logout",
    name: "logout",
    meta: { title: "Logout", authRequired: true },
    component: () => import("../views/account/logout")
  },
  {
    path: '/auth/start',
    name: 'auth-start',
    component: () => import('@/views/account/auth-start.vue'),
    meta: { title: "Create new account", guestOnly: true }
  },
  {
    path: '/auth/continue',
    name: 'auth-complete',
    component: () => import('@/views/account/auth-complete.vue'),
    meta: { title: "Register", guestOnly: true }
  },
  {
    path: '/auth/continue/:token',
    name: 'auth-complete-token',
    component: () => import('@/views/account/auth-complete.vue'),
    meta: { title: "Finish registration", guestOnly: true }
  },
  {
    path: '/auth/callback',
    name: 'sso-callback',
    component: () => import('@/views/account/sso-callback.vue'),
    meta: { title: "SSO Login", guestOnly: true }
  },
  {
    path: '/auth/forgot',
    name: 'auth-forgot',
    component: () => import('@/views/account/forgot-password.vue'),
    meta: { title: "Forget password", guestOnly: true },
  },
  {
    path: '/auth/reset',
    name: 'auth-reset',
    component: () => import('@/views/account/reset-password.vue'),
    meta: { title: "Reset password", guestOnly: true },
  },

  // ===== Workspaces =====
  {
    path: "/workspaces/new",
    name: "workspaces.create",
    meta: { title: "Create workspace", authRequired: true },
    component: () => import("@/views/workspaces/CreateNew.vue"),
  },
  {
    path: "/workspaces",
    name: "workspaces",
    component: () => import("@/views/workspaces/Select.vue"),
    meta: { requiresAuth: true }
  },

  // ===== Files =====
  {
    path: "/files",
    name: "files",
    meta: { title: "Files", authRequired: true },
    component: () => import("../views/files/FilesPage.vue")
  },

  // ===== Orders (OMS) =====
  {
    path: "/reports",
    name: "reports",
    meta: { title: "Reports", authRequired: true },
    component: () => import("../views/reports/Reports.vue"),
  },
  {
    path: "/orders",
    name: "orders.list",
    meta: { title: "Orders", authRequired: true },
    component: () => import("../views/orders/OrdersList.vue")
  },
  {
    path: "/returns",
    name: "returns.list",
    meta: { title: "Returns", authRequired: true },
    component: () => import("../views/orders/ReturnsList.vue")
  },
  {
    path: "/orders/:order_id",
    name: "orders.show",
    meta: { title: "Order Details", authRequired: true },
    component: () => import("../views/orders/OrderDetails.vue")
  },

  // ===== Marketplace'y (Allegro, Empik, Erli) =====
  {
    path: "/marketplace/:provider",
    name: "marketplace.show",
    meta: { title: "Marketplace", authRequired: true },
    component: () => import("../views/marketplace/MarketplacePage.vue")
  },
  {
    // Adres powrotu z logowania OAuth — musi zgadzać się z tym w aplikacji marketplace'u.
    path: "/integrations/:provider/callback",
    name: "marketplace.oauth-callback",
    meta: { title: "Marketplace", authRequired: true },
    component: () => import("../views/marketplace/OAuthCallback.vue")
  },

  // ===== Audit log =====
  {
    path: "/audit-log",
    name: "audit.list",
    meta: { title: "Audit log", authRequired: true },
    component: () => import("../views/audit/AuditList.vue")
  },

  // Plugin routes are registered dynamically from the plugin-extensions
  // registry in main.js — they live in `plugins/<id>/Frontend/Admin/`, not
  // here. See `src/plugin-extensions/index.js`.

  // ===== Product Templates =====
  {
    path: "/templates",
    name: "templates.list",
    meta: { title: "Product Templates", authRequired: true },
    component: () => import("../views/templates/TemplatesList.vue")
  },
  {
    path: "/templates/new",
    name: "templates.create",
    meta: { title: "New Template", authRequired: true },
    component: () => import("../views/templates/TemplateForm.vue")
  },
  {
    path: "/templates/:template_id",
    name: "templates.edit",
    meta: { title: "Edit Template", authRequired: true },
    component: () => import("../views/templates/TemplateForm.vue")
  },
  {
    path: "/templates/:template_id/attributes",
    name: "templates.attributes",
    meta: { title: "Template Attributes", authRequired: true },
    component: () => import("../views/templates/AttributesList.vue")
  },
  {
    path: "/templates/:template_id/grid",
    name: "templates.grid",
    meta: { title: "Attribute Grid Layout", authRequired: true },
    component: () => import("../views/templates/AttributeGridEditor.vue")
  },

  // ===== Price Lists =====
  {
    path: "/price-lists",
    name: "price-lists.list",
    meta: { title: "Price Lists", authRequired: true, requiresFinancials: true },
    component: () => import("../views/pricing/PriceListsList.vue")
  },
  {
    path: "/price-lists/new",
    name: "price-lists.create",
    meta: { title: "New Price List", authRequired: true, requiresFinancials: true },
    component: () => import("../views/pricing/PriceListForm.vue")
  },
  {
    path: "/price-lists/:rule_id",
    name: "price-lists.edit",
    meta: { title: "Edit Price List", authRequired: true, requiresFinancials: true },
    component: () => import("../views/pricing/PriceListForm.vue")
  },

  // ===== Marketing =====
  {
    path: "/marketing/campaigns",
    name: "marketing.campaigns.list",
    meta: { title: "Marketing", authRequired: true },
    component: () => import("../views/marketing/campaigns/CampaignsList.vue")
  },
  {
    path: "/marketing/campaigns/new",
    name: "marketing.campaigns.create",
    meta: { title: "New Campaign", authRequired: true },
    component: () => import("../views/marketing/campaigns/CampaignForm.vue")
  },
  {
    path: "/marketing/campaigns/:campaign_uuid",
    name: "marketing.campaigns.edit",
    meta: { title: "Edit Campaign", authRequired: true },
    component: () => import("../views/marketing/campaigns/CampaignForm.vue")
  },
  {
    path: "/marketing/coupons",
    name: "marketing.coupons.list",
    meta: { title: "Coupons", authRequired: true, requiresFinancials: true },
    component: () => import("../views/marketing/coupons/CouponsList.vue")
  },

  // ===== Products (PIM) =====
  {
    path: "/products",
    name: "products.list",
    meta: { title: "Products", authRequired: true },
    component: () => import("../views/products/ProductsList.vue")
  },
  {
    path: "/products/new",
    name: "products.create",
    meta: { title: "New Product", authRequired: true },
    component: () => import("../views/products/ProductForm.vue")
  },
  {
    path: "/products/:product_id",
    name: "products.edit",
    meta: { title: "Edit Product", authRequired: true },
    component: () => import("../views/products/ProductForm.vue")
  },
  {
    path: "/categories",
    name: "categories.list",
    meta: { title: "Categories", authRequired: true },
    component: () => import("../views/products/CategoriesList.vue")
  },

  // ===== Inventory =====
  {
    path: "/inventory",
    name: "inventory.list",
    meta: { title: "Inventory", authRequired: true },
    component: () => import("../views/inventory/InventoryList.vue")
  },
  {
    path: "/inventory/warehouses",
    name: "inventory.warehouses",
    meta: { title: "Warehouses", authRequired: true },
    component: () => import("../views/inventory/WarehousesList.vue")
  },
  {
    path: "/inventory/suppliers",
    name: "inventory.suppliers",
    meta: { title: "Suppliers", authRequired: true },
    component: () => import("../views/inventory/SuppliersList.vue")
  },
  {
    path: "/inventory/reports",
    name: "inventory.reports",
    meta: { title: "Inventory Reports", authRequired: true },
    component: () => import("../views/inventory/Reports.vue")
  },
  {
    path: "/inventory/documents",
    name: "inventory.documents",
    meta: { title: "Inventory Documents", authRequired: true },
    component: () => import("../views/inventory/DocumentsList.vue")
  },
  {
    path: "/inventory/documents/new",
    name: "inventory.documents.create",
    meta: { title: "New Document", authRequired: true },
    component: () => import("../views/inventory/DocumentForm.vue")
  },
  {
    path: "/inventory/documents/:document_id",
    name: "inventory.documents.show",
    meta: { title: "Document Details", authRequired: true },
    component: () => import("../views/inventory/DocumentDetails.vue")
  },
  {
    path: "/inventory/documents/:document_id/edit",
    name: "inventory.documents.edit",
    meta: { title: "Edit Document", authRequired: true },
    component: () => import("../views/inventory/DocumentForm.vue")
  },
  {
    path: "/inventory/:item_id/movements",
    name: "inventory.movements",
    meta: { title: "Stock Movements", authRequired: true },
    component: () => import("../views/inventory/MovementsList.vue")
  },

  // ===== Customers (CRM) =====
  {
    path: "/customers",
    name: "customers.list",
    meta: { title: "Customers", authRequired: true },
    component: () => import("../views/customers/CustomersList.vue")
  },
  {
    path: "/customers/groups",
    name: "customer-groups.list",
    meta: { title: "Customer Groups", authRequired: true },
    component: () => import("../views/customers/CustomerGroupsList.vue")
  },
  {
    path: "/customers/groups/applications",
    name: "customer-groups.applications",
    meta: { title: "Group Applications", authRequired: true },
    component: () => import("../views/customers/CustomerGroupApplications.vue")
  },
  {
    path: "/customers/groups/new",
    name: "customer-groups.create",
    meta: { title: "New Customer Group", authRequired: true },
    component: () => import("../views/customers/CustomerGroupForm.vue")
  },
  {
    path: "/customers/groups/:group_id",
    name: "customer-groups.edit",
    meta: { title: "Edit Customer Group", authRequired: true },
    component: () => import("../views/customers/CustomerGroupForm.vue")
  },
  {
    path: "/customers/:customer_id",
    name: "customers.show",
    meta: { title: "Customer Details", authRequired: true },
    component: () => import("../views/customers/CustomerDetails.vue")
  },

  // ===== Shipping =====
  {
    path: "/shipments",
    name: "shipments.list",
    meta: { title: "Shipments", authRequired: true },
    component: () => import("../views/shipping/ShipmentsList.vue")
  },
  {
    path: "/shipments/:shipment_id",
    name: "shipments.show",
    meta: { title: "Shipment Details", authRequired: true },
    component: () => import("../views/shipping/ShipmentDetails.vue")
  },
  {
    path: "/print-jobs",
    name: "print-jobs.list",
    meta: { title: "Print Jobs", authRequired: true },
    component: () => import("../views/shipping/PrintJobsList.vue")
  },
  {
    // Standalone device-pairing endpoint hit by the desktop print agent.
    // The agent generates a `device_code` and redirects the browser here;
    // we POST a new printer in the user's selected shop and bounce the
    // browser back to the agent via `lesscommerce-print-agent://paired?…`.
    path: "/print-agent/pair",
    name: "print-agent.pair",
    meta: { title: "Powiąż drukarkę", authRequired: true },
    component: () => import("../views/account/PrintAgentPair.vue")
  },

  // ===== Payments =====
  {
    path: "/payments",
    name: "payments.list",
    meta: { title: "Payments", authRequired: true },
    component: () => import("../views/payments/PaymentsList.vue")
  },

  // ===== Shops (workspace-level — no sidebar) =====
  {
    path: "/shops",
    name: "shops.list",
    meta: { title: "Shops", authRequired: true, hideSidebar: true },
    component: () => import("../views/shops/ShopsList.vue")
  },
  {
    path: "/shop/settings",
    name: "shop.settings",
    meta: { title: "Shop Settings", authRequired: true },
    component: () => import("../views/shops/ActiveShopSettings.vue")
  },
  {
    path: "/shops/:shop_id/settings",
    name: "shops.settings",
    meta: { title: "Shop Settings", authRequired: true },
    component: () => import("../views/shops/ShopSettings.vue")
  },
  // Legacy redirects — Integracje were briefly a separate route, now back inside Ustawienia.
  {
    path: "/shop/integrations",
    redirect: "/shop/settings?section=accounting"
  },
  {
    path: "/shops/:shop_id/integrations",
    redirect: to => `/shops/${to.params.shop_id}/settings?section=accounting`
  },

  // ===== Users & Settings (workspace-level — no sidebar) =====
  {
    path: "/users",
    name: "users.list",
    meta: { title: "Users", authRequired: true, hideSidebar: true },
    component: () => import("../views/users/users-list")
  },
  {
    path: "/settings",
    name: "settings.workspace",
    meta: { title: "Workspace Settings", authRequired: true, hideSidebar: true },
    component: () => import("../views/workspaces/SettingsView.vue")
  },
  {
    path: "/user/settings",
    name: "settings.user",
    meta: { title: "User Settings", authRequired: true, hideSidebar: true },
    component: () => import("../views/settings/SettingsCard.vue")
  },

  // ===== Subscriptions / Billing (workspace-level — no sidebar) =====
  {
    path: "/subscriptions",
    name: "subscriptions",
    meta: { title: "Subscriptions", authRequired: true, hideSidebar: true },
    component: () => import("../views/subscriptions/subscription-cart")
  },
];
